import React, { useState, useMemo, useEffect, useRef, ReactNode } from 'react';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import { Config } from '@/config';
import {
  errorModalTextRecoil,
  completeModalRecoil,
  modifyModalRecoil,
  addressModalRecoil,
  facilitiesAddModalRecoil,
} from '@/atom/ModalAtom';
import { siteListRecoil } from '@/atom/SiteAtom';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import LS from '@/styles/layoutStyled';
import CS from '@/styles/componentStyled';
import BS from '@/styles/btnStyled';
import IS from '@/styles/inputStyled';
import ModalType1 from '@/modal/ModalType1';
import AddressModal from './AddressModal';
import { timeData } from '@/data/timeData';
import {
  hangjungdong,
  SidoDto,
  SigunguDto,
  DongDto,
} from '@/data/hangjungdong';
import { SiteDto, SiteSearchDto } from '@/model/site.dto';
import { FacilitiesDto, ManagerDto } from '@/model/facilities.dto';
import { FacilityApi } from '@/sdk/FacilityApi';
import { UploadApi } from '@/sdk/UploadApi';
import {
  CreateFacilityStep2Dto,
  FacilityControllerGetFacilityDuplicateParams,
  FileUploadDto,
  FacilityManagerDto,
} from '@/sdk/data-contracts';

const FacilitiesAddModal = () => {
  const facilityApi = new FacilityApi({ baseURL: Config.API_URL });
  const uploadApi = new UploadApi({ baseURL: Config.API_URL });
  const navigate = useNavigate();

  const setErrorModalText = useSetRecoilState(errorModalTextRecoil);
  const setCompleteModalState = useSetRecoilState(completeModalRecoil);
  const setFacilitiesAddModalState = useSetRecoilState(
    facilitiesAddModalRecoil,
  );
  const [addressModalState, setAddressModalState] =
    useRecoilState(addressModalRecoil);
  const [siteListState, setSiteListState] = useRecoilState(siteListRecoil);

  const [number, setNumber] = useState<string>('');
  const [numberDuplication, setNumberDuplication] = useState<boolean>(false);
  const [name, setName] = useState<string>('');
  const [purpose, setPurpose] = useState<string>('');
  const [structure, setStructure] = useState<string>('');
  const [condition, setCondition] = useState<string>('');
  const [siteId, setSiteId] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [detail, setDetail] = useState<string>('');
  const [sido, setSido] = useState<string>('');
  const [sigungu, setSigungu] = useState<string>('');
  const [eupmyeondong, setEupmyeondong] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');
  const [fileUrl, setFileUrl] = useState<string>('');
  const [memo, setMemo] = useState<string>('');
  const [managers, setManagers] = useState<ManagerDto[]>([
    {
      isCheck: false,
      isResponsibility: true,
      department: '',
      name: '',
      phone: '',
      isSmsReception: false,
      receptionTime: '24',
      isCommunicationError: false,
      isSensorError: false,
    },
  ]);
  const [checkItems, setCheckItems] = useState<number[]>([]);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const handleManagerPlus = () => {
    const row = {
      isCheck: false,
      isResponsibility: false,
      department: '',
      name: '',
      phone: '',
      isSmsReception: false,
      receptionTime: '24',
      isCommunicationError: false,
      isSensorError: false,
    };

    setManagers((prev) => [...prev, row]);
  };

  const handleNumberDuplication = async () => {
    if (number.trim() === '') {
      setErrorModalText('시설 번호를 입력해 주세요.');
      return;
    }

    const dto: FacilityControllerGetFacilityDuplicateParams = {
      number,
    };

    try {
      const data = await facilityApi.facilityControllerGetFacilityDuplicate(
        dto,
        {
          headers: {
            Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
            'Content-Type': 'application/json',
          },
        },
      );
      if (data.data.statusCode === 200) {
        setErrorModalText('해당 시설 번호는 존재합니다.');
      }
      if (data.data.statusCode === 404) {
        setNumberDuplication(true);
      }
    } catch (err: any) {}
  };

  const handleAddressChange = (data: any) => {
    setAddress(
      data.jibunAddress === '' ? data.autoJibunAddress : data.jibunAddress,
    );
    setDetail('');
    setSido(data.sido);
    setSigungu(data.sigungu);
    setEupmyeondong(data.bname);
  };

  const handleFileUploadBtnClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const dto: FileUploadDto = {
        file: e.target.files[0],
      };
      try {
        const { data } = await uploadApi.fileuploadControllerUploadFile(dto);
        setFileName(e.target.files[0].name);
        setFileUrl(String(data.file));
      } catch (err: any) {}
    }
  };

  const handleFileDel = () => {
    setFileUrl('');
    setFileName('');
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLSelectElement | HTMLInputElement>,
    index: number,
    fieldName: keyof ManagerDto,
  ) => {
    const updatedManagers = [...managers];

    if (e.target instanceof HTMLInputElement && e.target.type === 'radio') {
      updatedManagers.forEach((manager, i) => {
        if (i !== index && manager[fieldName] === true) {
          updatedManagers[i][fieldName] = false as never;
        }
      });
      updatedManagers[index][fieldName] = e.target.checked as never;
    } else if (
      e.target instanceof HTMLInputElement &&
      e.target.type === 'checkbox'
    ) {
      updatedManagers[index][fieldName] = e.target.checked as never;
    } else {
      updatedManagers[index][fieldName] = e.target.value as never;
    }

    setManagers(updatedManagers);
  };

  const handleSingleCheck = (checked: boolean, id: number) => {
    if (checked) {
      setCheckItems((prev) => [...prev, id]);
    } else {
      setCheckItems((prev) => prev.filter((el) => el !== id));
    }
  };

  const handleSelectDelete = () => {
    if (managers.length === checkItems.length) {
      setErrorModalText('최소 한 명의 담당자는 있어야 합니다.');
      return;
    }
    const updatedManagers = managers.filter(
      (_, index) => !checkItems.includes(index),
    );
    setManagers(updatedManagers);
    setCheckItems([]);
  };

  const handleSubmit = async () => {
    if (
      name.trim() === '' ||
      condition.trim() === '' ||
      siteId.trim() === '' ||
      address.trim() === ''
    ) {
      setErrorModalText('빈 칸을 확인해 주세요.');
      return;
    }

    const managersRowsTrimCheck = managers.every(
      (manager) =>
        manager.department.trim() !== '' &&
        manager.name.trim() !== '' &&
        manager.phone.trim() !== '',
    );

    const phoneRegex =
      /^(010\d{8}|02\d{8}|070\d{8}|0507\d{8}|1[5-9]\d{6}|043\d{7})$/;

    const managersPhoneTypeCheck = managers.every((manager) =>
      phoneRegex.test(manager.phone),
    );

    if (!managersRowsTrimCheck) {
      setErrorModalText('빈 칸을 확인해 주세요.');
      return;
    }

    if (!numberDuplication) {
      setErrorModalText('시설 번호 중복 확인을 해주세요.');
      return;
    }

    if (!managersPhoneTypeCheck) {
      setErrorModalText('담당자 연락처를 확인해 주세요.');
      return;
    }

    const dto: CreateFacilityStep2Dto = {
      number,
      name,
      purpose,
      structure,
      condition,
      siteId: Number(siteId),
      address,
      detail,
      sido,
      sigungu,
      eupmyeondong,
      fileUrl,
      fileName,
      memo,
      managers: managers.map(({ isCheck, ...rest }) => rest),
    };

    try {
      await facilityApi.facilityControllerCreateFacility(dto, {
        headers: {
          Authorization: `Bearer ${String(sessionStorage.getItem('accessToken'))}`,
          'Content-Type': 'application/json',
        },
      });
      setCompleteModalState(true);
    } catch (err: any) {}
  };

  return (
    <>
      <ModalType1 width={1000}>
        <div className="title-box flex aic jcsb">
          <h1>시설물 등록</h1>
          <button
            className="x-btn img-box"
            onClick={() => setFacilitiesAddModalState(false)}
          >
            <img src="/img/ico_x_444.svg" alt="" />
          </button>
        </div>
        <div className="cnt-area flex fdc">
          <div className="cnt-wrap flex fdc">
            <div className="cnt-box flex fdc">
              <div className="cnt-title-box flex jcsb aie">
                <div className="left-box">
                  <h2 className="tm">기본 정보</h2>
                </div>
                <div className="right-box flex">
                  <h3>
                    <span className="inblock">*</span>필수 항목
                  </h3>
                </div>
              </div>
              <CS.TableType2>
                <table>
                  <tbody>
                    <tr>
                      <th>
                        시설 번호<span className="star-point">*</span>
                      </th>
                      <td>
                        <div className="td-box">
                          <IS.TextInputType1
                            style={{ width: 190 }}
                            type="text"
                            value={number}
                            onChange={(e) => {
                              setNumber(e.target.value);
                              setNumberDuplication(false);
                            }}
                          />
                          <BS.Btn3Type3
                            type="button"
                            onClick={handleNumberDuplication}
                            disabled={numberDuplication}
                          >
                            {numberDuplication ? '완료' : '중복 조회'}
                          </BS.Btn3Type3>
                        </div>
                      </td>
                      <th>
                        시설명<span className="star-point">*</span>
                      </th>
                      <td>
                        <IS.TextInputType1
                          style={{ width: 190 }}
                          type="text"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>건물 용도</th>
                      <td>
                        <IS.TextInputType1
                          style={{ width: 190 }}
                          type="text"
                          value={purpose}
                          onChange={(e) => setPurpose(e.target.value)}
                        />
                      </td>
                      <th>구조</th>
                      <td>
                        <IS.TextInputType1
                          style={{ width: 190 }}
                          type="text"
                          value={structure}
                          onChange={(e) => setStructure(e.target.value)}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>
                        건물 상태<span className="star-point">*</span>
                      </th>
                      <td>
                        <IS.TextInputType1
                          style={{ width: 190 }}
                          type="text"
                          value={condition}
                          onChange={(e) => setCondition(e.target.value)}
                        />
                      </td>
                      <th>
                        사이트<span className="star-point">*</span>
                      </th>
                      <td>
                        <IS.SelectType1 style={{ width: 190 }}>
                          <select
                            value={siteId}
                            onChange={(e) => setSiteId(e.target.value)}
                            className="selected"
                          >
                            <option value="">사이트</option>
                            {siteListState?.map((list) => (
                              <option key={list.id} value={list.id}>
                                {list.name}
                              </option>
                            ))}
                          </select>
                          <span className="img-box">
                            <img src="/img/arrow_bottom_555.svg" alt="" />
                          </span>
                        </IS.SelectType1>
                      </td>
                    </tr>
                    <tr>
                      <th>
                        주소<span className="star-point">*</span>
                      </th>
                      <td>
                        <div className="td-wrap">
                          <div className="td-box">
                            <IS.TextInputType1
                              style={{ width: 376 }}
                              type="text"
                              placeholder="기본주소"
                              readOnly={true}
                              value={address}
                              onChange={(e) => setAddress(e.target.value)}
                            />
                            <BS.Btn3Type3
                              type="button"
                              onClick={() => setAddressModalState(true)}
                            >
                              주소 검색
                            </BS.Btn3Type3>
                          </div>
                          <div className="td-box">
                            <IS.TextInputType1
                              style={{ width: 475 }}
                              type="text"
                              placeholder="상세주소"
                              value={detail}
                              onChange={(e) => setDetail(e.target.value)}
                            />
                          </div>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>상세동</th>
                      <td colSpan={3}>
                        <div className="td-box">
                          <IS.TextInputType1
                            style={{ width: 153 }}
                            type="text"
                            placeholder="시/도"
                            disabled={true}
                            value={sido}
                          />
                          <IS.TextInputType1
                            style={{ width: 153 }}
                            type="text"
                            placeholder="시군구"
                            disabled={true}
                            value={sigungu}
                          />
                          <IS.TextInputType1
                            style={{ width: 153 }}
                            type="text"
                            placeholder="읍/면/동"
                            disabled={true}
                            value={eupmyeondong}
                          />
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>첨부파일</th>
                      <td colSpan={3}>
                        <div className="td-box">
                          <BS.Btn3Type5
                            type="button"
                            onClick={handleFileUploadBtnClick}
                          >
                            파일 선택
                          </BS.Btn3Type5>
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileChange}
                            style={{ display: 'none' }}
                          />
                          <IS.FileTextType1>
                            {fileName === '' && (
                              <span className="no-file-text">
                                선택된 파일 없음
                              </span>
                            )}
                            {fileName !== '' && (
                              <span className="file-text">{fileName}</span>
                            )}
                            {fileName !== '' && (
                              <button
                                className="img-box"
                                type="button"
                                onClick={handleFileDel}
                              >
                                <img src="/img/ico_x_FC5B5B.svg" alt="" />
                              </button>
                            )}
                          </IS.FileTextType1>
                        </div>
                      </td>
                    </tr>
                    <tr>
                      <th>메모</th>
                      <td colSpan={3}>
                        <IS.TextInputType1
                          style={{ width: 475 }}
                          type="text"
                          value={memo}
                          onChange={(e) => setMemo(e.target.value)}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </CS.TableType2>
            </div>
            <div className="cnt-box flex fdc">
              <div className="cnt-title-box flex jcsb aie">
                <div className="left-box">
                  <h2 className="tm">시설 담당자 정보</h2>
                </div>
                <div className="right-box flex">
                  <BS.Btn1Type1 type="button" onClick={handleManagerPlus}>
                    추가
                  </BS.Btn1Type1>
                  <BS.Btn1Type2 type="button" onClick={handleSelectDelete}>
                    선택 삭제
                  </BS.Btn1Type2>
                </div>
              </div>
              <CS.TableType1>
                <table>
                  <thead>
                    <tr>
                      <th></th>
                      <th>책임</th>
                      <th>
                        부서<span className="star-point">*</span>
                      </th>
                      <th>
                        성명<span className="star-point">*</span>
                      </th>
                      <th>
                        연락처<span className="star-point">*</span>
                      </th>
                      <th>SMS 수신</th>
                      <th>수신 시간</th>
                      <th>통신이상</th>
                      <th>센서이상</th>
                    </tr>
                  </thead>
                  <tbody>
                    {managers.map((list: ManagerDto, index) => (
                      <tr key={index}>
                        <td>
                          <IS.CheckBoxType1>
                            <input
                              type="checkbox"
                              id={`fam_cv1-${index}`}
                              onChange={(e) =>
                                handleSingleCheck(e.target.checked, index)
                              }
                              checked={
                                checkItems.includes(index) ? true : false
                              }
                            />
                            <label
                              htmlFor={`fam_cv1-${index}`}
                              className="img-box"
                            >
                              <img src="/img/ico_check_1.svg" alt="" />
                            </label>
                          </IS.CheckBoxType1>
                        </td>
                        <td>
                          <IS.CheckBoxType2>
                            <input
                              type="radio"
                              id={`fcv2-${index}`}
                              name="master"
                              defaultChecked={list.isResponsibility}
                              onChange={(e) =>
                                handleInputChange(e, index, 'isResponsibility')
                              }
                            />
                            <label
                              htmlFor={`fcv2-${index}`}
                              className="img-box"
                            >
                              <img src="/img/ico_check_3.svg" alt="" />
                            </label>
                          </IS.CheckBoxType2>
                        </td>
                        <td>
                          <IS.TextInputType1
                            style={{ width: 110 }}
                            type="text"
                            value={list.department}
                            onChange={(e) =>
                              handleInputChange(e, index, 'department')
                            }
                          />
                        </td>
                        <td>
                          <IS.TextInputType1
                            style={{ width: 86 }}
                            type="text"
                            value={list.name}
                            onChange={(e) =>
                              handleInputChange(e, index, 'name')
                            }
                          />
                        </td>
                        <td>
                          <IS.TextInputType1
                            style={{ width: 140 }}
                            type="text"
                            value={list.phone}
                            onChange={(e) => {
                              const numericValue = e.target.value.replace(
                                /\D/g,
                                '',
                              );
                              const updatedEvent = {
                                ...e,
                                target: { ...e.target, value: numericValue },
                              };
                              handleInputChange(updatedEvent, index, 'phone');
                            }}
                          />
                        </td>
                        <td>
                          <IS.CheckBoxType1>
                            <input
                              type="checkbox"
                              id={`fcv3-${index}`}
                              defaultChecked={list.isSmsReception}
                              onChange={(e) =>
                                handleInputChange(e, index, 'isSmsReception')
                              }
                            />
                            <label
                              htmlFor={`fcv3-${index}`}
                              className="img-box"
                            >
                              <img src="/img/ico_check_1.svg" alt="" />
                            </label>
                          </IS.CheckBoxType1>
                        </td>
                        <td>
                          <IS.SelectType1 style={{ width: 153 }}>
                            <select
                              value={list.receptionTime}
                              onChange={(e) =>
                                handleInputChange(e, index, 'receptionTime')
                              }
                              className="selected"
                            >
                              {timeData.map((timeData, index) => (
                                <option key={index} value={timeData.timeValue}>
                                  {timeData.time}
                                </option>
                              ))}
                            </select>
                            <span className="img-box">
                              <img src="/img/arrow_bottom_555.svg" alt="" />
                            </span>
                          </IS.SelectType1>
                        </td>
                        <td>
                          <IS.CheckBoxType1>
                            <input
                              type="checkbox"
                              id={`mcv4-${index}`}
                              defaultChecked={list.isCommunicationError}
                              onChange={(e) =>
                                handleInputChange(
                                  e,
                                  index,
                                  'isCommunicationError',
                                )
                              }
                            />
                            <label
                              htmlFor={`mcv4-${index}`}
                              className="img-box"
                            >
                              <img src="/img/ico_check_1.svg" alt="" />
                            </label>
                          </IS.CheckBoxType1>
                        </td>
                        <td>
                          <IS.CheckBoxType1>
                            <input
                              type="checkbox"
                              id={`mcv5-${index}`}
                              defaultChecked={list.isSensorError}
                              onChange={(e) =>
                                handleInputChange(e, index, 'isSensorError')
                              }
                            />
                            <label
                              htmlFor={`mcv5-${index}`}
                              className="img-box"
                            >
                              <img src="/img/ico_check_1.svg" alt="" />
                            </label>
                          </IS.CheckBoxType1>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CS.TableType1>
            </div>
          </div>
          <div className="btn-wrap">
            <BS.Btn2Type3
              type="button"
              onClick={() => setFacilitiesAddModalState(false)}
            >
              취소
            </BS.Btn2Type3>
            <BS.Btn2Type4 type="button" onClick={handleSubmit}>
              등록
            </BS.Btn2Type4>
          </div>
        </div>
      </ModalType1>
      {addressModalState && (
        <AddressModal onAddressChange={handleAddressChange} />
      )}
    </>
  );
};

export default FacilitiesAddModal;
